import { Box, Center, Flex, Show, Text } from '@chakra-ui/react';
import type { Msg, User } from '@halo/chat-sdk';
import {
  CloseIcon,
  ReplyPreviewIcon,
  FileTypeIcon,
  MessageEditIcon,
  StickerTypeIcon,
} from '@halo/ui-chat-react';
import { isStickerMessage, MsgPayloadType } from '@halo/chat-sdk';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

export interface MessageComposerReplyPreviewProps {
  children: ReactNode;
  message?: Msg | null;
  user?: User | null;
  isEditing?: boolean;
  onClose?: () => void;
}

function MessageComposerReplyPreview({
  children,
  message,
  user,
  isEditing,
  onClose,
}: MessageComposerReplyPreviewProps) {
  const { t } = useTranslation();
  const preventDefault = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
  }, []);

  const isFile = useMemo(() => {
    return (
      message?.payload_type === MsgPayloadType.FILE ||
      message?.payload_type === MsgPayloadType.IMAGE
    );
  }, [message?.payload_type]);

  const isSticker = useMemo(() => isStickerMessage(message), [message]);

  return (
    <Flex
      className="MessageComposerReplyPreview"
      alignItems="center"
      gap="1"
      justify="space-between"
      pb={message ? '2.5' : 0}
      onClick={preventDefault}
      onMouseDown={preventDefault}
      css={{
        animation: 'fade-in-up 0.3s ease-in-out',
        transition: 'all 0.3s',
      }}
      overflow="hidden"
    >
      <Show when={message}>
        <Center color="brand.100" pb="2">
          {/* <ForwardPreviewIcon */}
          {isEditing ? <MessageEditIcon /> : <ReplyPreviewIcon />}
        </Center>
        <Box
          flex="1"
          pb="1.5"
          borderBottom="1px solid var(--colors-border-subtle)"
          overflow="hidden"
        >
          <Flex
            alignItems="center"
            justify="space-between"
            gap="2"
            pl="2"
            borderLeft="2px solid var(--colors-brand-100)"
            overflow="hidden"
          >
            <Flex flex="1" alignItems="center" gap="1" overflow="hidden">
              {isSticker && (
                <StickerTypeIcon
                  sticker_id={message?.payload?.id}
                  category={message?.payload?.stickerSetInfo?.category}
                />
              )}
              {isFile && (
                <FileTypeIcon
                  style={{ borderRadius: 1 }}
                  extType={message?.payload?.ext}
                  src={message?.payload?.thumbnail}
                />
              )}
              <Flex flex="1" direction="column" gap="1" overflow="hidden">
                <Text
                  fontSize="xs"
                  color="brand.100"
                  fontWeight="medium"
                  lineHeight="normal"
                  truncate
                >
                  {isEditing
                    ? t('action.editMessage')
                    : t('action.replyTo', { name: user?.name })}
                </Text>
                <Text
                  fontSize="xs"
                  color="fg.muted"
                  lineHeight="normal"
                  truncate
                  fontWeight="medium"
                >
                  {children}
                </Text>
              </Flex>
            </Flex>
            <Center cursor="pointer">
              <CloseIcon onClick={onClose} />
            </Center>
          </Flex>
        </Box>
      </Show>
    </Flex>
  );
}

const MemoizedMessageComposerReplyPreview = memo(MessageComposerReplyPreview);

export { MemoizedMessageComposerReplyPreview as MessageComposerReplyPreview };
